import React, { useMemo, useRef, useEffect } from "react";
import { styled } from "../../../stitches.config";
import { usePodcastPlayer, usePlayerState, formatTime, usePlayerInit, PODCASTS_PLAYERS } from "../../../context/PodcastPlayerContext";

import PodcastPlayerVolume from './PodcastPlayerVolume';
import PodcastNextEpisodeButton from './PodcastNextEpisodeButton';
import PodcastSkipTime from './PodcastSkipTime';

import EqualizerMask from "../../../assets/img/podcastPlayer/podcast_equalizer_mask.svg";

import { ReactComponent as PauseIcon } from "../../../assets/img/podcastPlayer/podcast_pause.svg?react";
import { ReactComponent as PlayIcon } from "../../../assets/img/podcastPlayer/podcast_play.svg?react";
import { ReactComponent as ReplayIcon } from "../../../assets/img/podcastPlayer/podcast_replay.svg?react";
import { ReactComponent as AdPlayIcon } from "../../../assets/img/podcastPlayer/podcast_ad.svg?react";

import { ReactComponent as NextIcon } from "../../../assets/img/podcastPlayer/podcast_next.svg?react";
import { Link } from "react-router-dom";

const PodcastPlayerWidget: React.FC<any> = (props) => {

    const { data } = props;

    const podcastContext = usePodcastPlayer();

    const { play, pause, seek, volume, setVolume, setEpisode, isPlayerInit } = podcastContext;

    const [videoId, playerInit] = usePlayerInit(data.podcast_article);

    const progress = usePlayerState<number>('progress', podcastContext, videoId);
    const duration = useMemo(() => videoId ? (PODCASTS_PLAYERS[videoId]?.duration || 0) : 0, [playerInit]);

    const isPlay = usePlayerState<boolean>('isPlay', podcastContext, videoId);
    const isCompleted = usePlayerState<boolean>('isCompleted', podcastContext, videoId);
    const isVideoLoading = usePlayerState<boolean>('isVideoLoading', podcastContext, videoId);
    const isAdPlay = usePlayerState<boolean>('isAdPlay', podcastContext, videoId);

    const isReady = useMemo(() => isPlayerInit && !isVideoLoading, [isPlayerInit, isVideoLoading]);

    const progressString = formatTime(progress, duration >= 3600);
    const durationString = useMemo(() => formatTime(duration), [duration]);

    const progressDiff = useMemo(() => duration - progress, [progress, duration]);

    const equalizerDiv = useRef<HTMLDivElement>(null);

    const prevEpisode = videoId != undefined ? PODCASTS_PLAYERS[videoId]?.prevEpisode : undefined;
    const nextEpisode = videoId != undefined ? PODCASTS_PLAYERS[videoId]?.nextEpisode : undefined;

    const changeProgress = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!equalizerDiv.current) return;

        const bounding = equalizerDiv.current.getBoundingClientRect();
        let position = e.clientX - bounding.left;
        let newProgress = position / bounding.width * duration;
        seek(newProgress, videoId);
    }

    const playAction = () => {
        return (!isPlay || isCompleted) ? play(videoId, isCompleted ? 0 : progress) : pause()
    }

    return (
        <WidgetContainer>
            <TimeInfo>
                <div>{isAdPlay ? "Reklama" : isReady ? progressString : "Ładowanie..."}</div>
                <div>{isReady || isAdPlay ? durationString : ""}</div>
            </TimeInfo>
            <Equalizer onClick={changeProgress} ref={equalizerDiv}>
                <div style={{ width: (progress / duration) * 100 + '%' }} />
            </Equalizer>
            <Controls>
                {prevEpisode && (
                    <Link to={`/${prevEpisode.episode_id}`}>
                        <ControlButton type="prev" onClick={() => setEpisode(prevEpisode)}>
                            <NextIcon />
                        </ControlButton>
                    </Link>
                )}
                <PlayButton onClick={playAction}>
                    {isAdPlay ? (
                        <AdPlayIcon className="adPlayButton" />
                    ) : (
                        <>
                            {!isPlay && !isCompleted && <PlayIcon style={{ transform: 'translateX(12.5%)' }} />}
                            {isPlay && <PauseIcon />}
                            {isCompleted && <ReplayIcon />}
                        </>
                    )}
                </PlayButton>
                {nextEpisode && (
                    <Link to={`/${nextEpisode.episode_id}`}>
                        <ControlButton onClick={() => setEpisode(nextEpisode)}>
                            <NextIcon />
                        </ControlButton>
                    </Link>
                )}
                <PodcastSkipTime
                    duration={duration}
                    progress={progress}
                    videoId={videoId}
                />
                {((progressDiff > 10 && !isCompleted) || !nextEpisode) && <PodcastPlayerVolume volume={volume} setVolume={setVolume} />}
                {(progressDiff <= 10 || isCompleted) && playerInit && nextEpisode && (
                    <Link to={`/${nextEpisode.episode_id}`}>
                        <PodcastNextEpisodeButton onClick={() => setEpisode(nextEpisode)} />
                    </Link>
                )}
            </Controls>
        </WidgetContainer>
    );
}

export default PodcastPlayerWidget;

const WidgetContainer = styled('div', {
    position: 'relative',
    marginTop: '80px'
});

const TimeInfo = styled('div', {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: '$8',
    fontSize: '14px',
    lineHeight: '$18',
    color: '$grayscale100',
    marginBottom: '1rem'
});

const Equalizer = styled('div', {
    display: 'flex',
    cursor: 'pointer',
    height: '30px',
    background: '$grayscale50',
    marginBottom: '2rem',
    maskImage: `url("${EqualizerMask}")`,
    '& > div': {
        background: '$primary'
    }
});

const Controls = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '24px',
    'svg': {
        fill: '$grayscale100',
    },
    'button:not(.podcastPlayerNav__nextEpisode)': {
        display: 'flex',
        background: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
    },
    '@bp4': {
        justifyContent: 'flex-start',
        marginTop: '32px',
    }
});

const PlayButton = styled('button', {
    width: '48px',
    height: '48px',
    borderRadius: '100%',
    border: '2px solid $grayscale25',
    '@bp4': {
        width: '64px',
        height: '64px',
    },
    '.adPlayButton': {
        width: '70%'
    }
});

const ControlButton = styled('button', {
    border: 'none',
    width: '16px',
    '@bp4': {
        width: 'auto'
    },
    variants: {
        type: {
            next: {
                marginLeft: '24px',
                '@bp4': {
                    marginLeft: '35px',
                }
            },
            prev: {
                marginRight: '24px',
                '@bp4': {
                    marginRight: '35px',
                },
                svg: {
                    transform: 'scaleX(-1)'
                }
            }
        }
    },
    defaultVariants: {
        type: 'next'
    }
});